import {
  throwFromApiErrorResponse,
  isConcurrentProcessingError,
  isInsufficientCreditsError,
  isRateLimitError,
} from './creditErrors';

export async function parseJsonErrorBody(response: Response): Promise<Record<string, unknown>> {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return {};
    }
    const data = await response.json();
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, unknown>;
    }
  } catch {
    // Fall through with empty payload.
  }
  return {};
}

export async function assertJsonOk(response: Response): Promise<void> {
  if (response.ok) return;
  const data = await parseJsonErrorBody(response);
  throwFromApiErrorResponse(response.status, data);
}

export interface CallAIEndpointOptions {
  idempotencyKey?: string;
  requestId?: string;
  signal?: AbortSignal;
}

export async function callAIEndpoint(
  endpoint: string,
  payload: object,
  options: CallAIEndpointOptions = {}
) {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (options.idempotencyKey) {
      headers['Idempotency-Key'] = options.idempotencyKey;
    }
    if (options.requestId) {
      headers['X-AIMaker-Request-ID'] = options.requestId;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: JSON.stringify(payload),
      signal: options.signal,
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text().catch(() => `HTTP ${response.status}`);
      const shortText = text.slice(0, 200);
      console.error('API request error: non-JSON response', response.status, shortText);
      throw new Error(`Server error (${response.status}). Please try again.`);
    }

    const data = await response.json();

    if (!response.ok || data.error) {
      if (typeof data.balance === 'number') {
        const { applyServerBalance } = await import('@/lib/client/applyServerBalance');
        applyServerBalance(data.balance);
      }
      if (!response.ok) {
        throwFromApiErrorResponse(response.status, data);
      }
      console.error('API Route Error:', { error: data.error });
      const err = new Error(data.error || `Request failed: ${response.status}`) as Error & {
        creditsRestored?: boolean;
        balance?: number;
      };
      if (data.creditsRestored === true) err.creditsRestored = true;
      if (typeof data.balance === 'number') err.balance = data.balance;
      throw err;
    }

    return data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    if (error instanceof Error && error.message.startsWith('Server error')) {
      throw error;
    }
    // Expected handled credit races — rethrow without console.error (avoids Next overlay)
    if (
      isConcurrentProcessingError(error) ||
      isInsufficientCreditsError(error) ||
      isRateLimitError(error)
    ) {
      throw error;
    }
    console.error('API request error:', error);
    throw error;
  }
}
